import Phaser from 'phaser';

const IDLE=6,WALK=8,ATTACK=6;
/** Continuous inverse warp creates whole-image poses: no cutout seams or loose limbs.
 * Shared texture cache is bounded by the original art catalog, never by actor IDs. */
export function addFrameMotion(scene:Phaser.Scene,image:Phaser.GameObjects.Image,phase=0): (time:number,speed:number,attacking:boolean)=>void {
  const sourceKey=image.texture.key,key='motion-frames:'+sourceKey;
  if(!scene.textures.exists(key)){
    const source=image.texture.getSourceImage() as HTMLImageElement;
    const height=Math.min(224,source.height),width=Math.max(1,Math.round(source.width*height/source.height));
    const input=document.createElement('canvas');input.width=width;input.height=height;
    const ctx=input.getContext('2d',{willReadFrequently:true})!;ctx.drawImage(source,0,0,width,height);
    const pixels=ctx.getImageData(0,0,width,height).data,pad=12,w=width+pad*2,h=height+pad*2;
    const canvas=document.createElement('canvas');canvas.width=w*(IDLE+WALK+ATTACK);canvas.height=h;
    const output=canvas.getContext('2d')!;
    for(let frame=0;frame<IDLE+WALK+ATTACK;frame++){
      const mode=frame<IDLE?'idle':frame<IDLE+WALK?'walk':'attack';
      const t=mode==='idle'?frame/IDLE:mode==='walk'?(frame-IDLE)/WALK:(frame-IDLE-WALK)/(ATTACK-1);
      const wave=Math.sin(t*Math.PI*2),swing=Math.sin(t*Math.PI),data=output.createImageData(w,h);
      for(let y=0;y<h;y++)for(let x=0;x<w;x++){
        const py=(y-pad)/height,px=(x-pad)/width,side=(px-.5)*2;
        const leg=Math.max(0,(py-.55)/.45),upper=Math.max(0,1-py/.65);
        const dx=mode==='walk'?wave*(leg*side*width*.055+upper*width*.013):mode==='attack'?swing*width*.065*(1-py):wave*width*.009*upper;
        const dy=mode==='walk'?Math.max(0,side*wave)*leg*height*.026:mode==='attack'?-swing*height*.014*upper:-wave*height*.006*Math.sin(Math.PI*Math.max(0,Math.min(1,py)));
        const sx=x-pad-dx,sy=y-pad-dy,ix=Math.floor(sx),iy=Math.floor(sy);
        if(ix<0||iy<0||ix>=width-1||iy>=height-1)continue;
        // Bilinear sampling in premultiplied alpha avoids dark fringes.
        const fx=sx-ix,fy=sy-iy,weights=[(1-fx)*(1-fy),fx*(1-fy),(1-fx)*fy,fx*fy],indices=[(iy*width+ix)*4,(iy*width+ix+1)*4,((iy+1)*width+ix)*4,((iy+1)*width+ix+1)*4];
        let alpha=0;const rgb=[0,0,0];
        for(let k=0;k<4;k++){const a=pixels[indices[k]!+3]!*weights[k]!;alpha+=a;for(let c=0;c<3;c++)rgb[c]!+=pixels[indices[k]!+c]!*a;}
        const dest=(y*w+x)*4;data.data[dest+3]=alpha;if(alpha>0)for(let c=0;c<3;c++)data.data[dest+c]=rgb[c]!/alpha;
      }
      output.putImageData(data,frame*w,0);
    }
    const texture=scene.textures.addCanvas(key,canvas)!;
    for(let i=0;i<IDLE+WALK+ATTACK;i++)texture.add(i,0,i*w,0,w,h);
    texture.get('__BASE').setSize(w,h);
    // Padding preserves the original source's ground anchor and aspect ratio.
    texture.customData={sourceWidth:source.width,sourceHeight:source.height,width,height,pad};
  }
  const meta=scene.textures.get(key).customData as {sourceWidth:number;sourceHeight:number;width:number;height:number;pad:number};
  const scale=image.scaleY*meta.sourceHeight/meta.height;
  const originX=(meta.pad+image.originX*meta.width)/(meta.width+meta.pad*2),originY=(meta.pad+image.originY*meta.height)/(meta.height+meta.pad*2);
  image.setTexture(key,0).setOrigin(originX,originY).setScale(scale).setData('frame-motion',true);
  return(time,speed,attacking)=>{
    const frame=attacking?IDLE+WALK+Math.min(ATTACK-1,Math.floor(((time+phase)%480)/80)):Math.abs(speed)>8?IDLE+Math.floor((time+phase)/90)%WALK:Math.floor((time+phase)/150)%IDLE;
    image.setFrame(frame);image.setData('motion-frame',frame);
  };
}

/** Residents breathe, turn and briefly gesture on staggered schedules. */
export function animateResident(scene:Phaser.Scene,image:Phaser.GameObjects.Image,phase=0):void {
  const frames=addFrameMotion(scene,image,phase);
  const update=(time:number)=>frames(time,0,(time+phase)%9300>8100);
  scene.events.on(Phaser.Scenes.Events.UPDATE,update);
  image.once(Phaser.GameObjects.Events.DESTROY,()=>scene.events.off(Phaser.Scenes.Events.UPDATE,update));
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN,()=>scene.events.off(Phaser.Scenes.Events.UPDATE,update));
}
